import { isJournalMaterial } from '@/lib/documentSource';
import type { Database } from '@/types/database.generated';
import {
  journalDisplayAppointment,
  journalKindLabel,
  type JournalLinkedAppointment,
} from '@/utils/journalHistoryFilters';

type JournalEntryType = Database['public']['Enums']['journal_entry_type'];

export type JournalExportStatus = 'finalized' | 'draft';
export type JournalExportAmendmentKind = 'correction' | 'addendum';

export interface JournalExportAttachment {
  documentId: string | null;
  filename: string;
  mimeType: string;
  createdAt: string;
  uploadedAt: string | null;
  uploadedBy: string | null;
  notes: string | null;
  bilagNumber?: number;
  zipFilename?: string;
  zipPath?: string;
}

export interface JournalExportAmendment {
  kind: JournalExportAmendmentKind;
  status: JournalExportStatus;
  content: string;
  createdAt: string;
  finalizedAt: string | null;
  practitionerName: string | null;
  attachments: JournalExportAttachment[];
}

export interface JournalExportEntry {
  clinicalDate: string;
  encounterAt: string | null;
  appointmentAt: string | null;
  serviceName: string | null;
  practitionerName: string | null;
  status: JournalExportStatus;
  title: string | null;
  content: string;
  createdAt: string;
  finalizedAt: string | null;
  attachments: JournalExportAttachment[];
  amendments: JournalExportAmendment[];
}

export interface JournalExportDocument {
  patientName: string;
  patientNumber: string | null;
  clinicName: string;
  exportedAt: string;
  entries: JournalExportEntry[];
}

export interface JournalBilagItem {
  number: number;
  documentId: string | null;
  filename: string;
  mimeType: string;
  createdAt: string;
  uploadedAt: string | null;
  uploadedBy: string | null;
  notes: string | null;
  zipFilename: string;
  zipPath: string;
  relatedClinicalDate: string | null;
  relatedServiceName: string | null;
}

const JOURNAL_ENTRY_TYPES: readonly JournalEntryType[] = [
  'generic',
  'dentist',
  'emdr_session',
  'psychology_session',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === 'string');
}

function parseStatus(value: unknown): JournalExportStatus {
  return value === 'finalized' ? 'finalized' : 'draft';
}

function parseEntryType(value: unknown): JournalEntryType {
  if (typeof value === 'string' && (JOURNAL_ENTRY_TYPES as readonly string[]).includes(value)) {
    return value as JournalEntryType;
  }
  return 'generic';
}

function parseAttachment(value: unknown): JournalExportAttachment | null {
  if (!isRecord(value)) return null;
  const filename = asString(value.filename);
  if (!filename) return null;
  if ('tags' in value && !isJournalMaterial(asStringArray(value.tags))) {
    return null;
  }
  return {
    documentId: asString(value.document_id),
    filename,
    mimeType: asString(value.mime_type) ?? 'application/octet-stream',
    createdAt: asString(value.created_at) ?? '',
    uploadedAt: asString(value.document_created_at) ?? asString(value.created_at),
    uploadedBy: asString(value.uploaded_by_name),
    notes: asString(value.notes),
  };
}

function parseAmendment(value: unknown): JournalExportAmendment | null {
  if (!isRecord(value)) return null;
  const createdAt = asString(value.created_at);
  if (!createdAt) return null;
  return {
    kind: 'correction',
    status: parseStatus(value.status ?? (value.finalized_at ? 'finalized' : 'draft')),
    content: typeof value.content_md === 'string' ? value.content_md : '',
    createdAt,
    finalizedAt: asString(value.finalized_at),
    practitionerName: asString(value.author_name_snapshot),
    attachments: asArray(value.attachments)
      .map(parseAttachment)
      .filter((row): row is JournalExportAttachment => row != null),
  };
}

function liveAppointment(entry: Record<string, unknown>): JournalLinkedAppointment | null {
  const starts = asString(entry.appointment_starts_at);
  const service = asString(entry.appointment_service_name);
  const clinician = asString(entry.appointment_clinician_name);
  if (!starts && !service && !clinician) return null;
  return {
    starts_at: starts ?? '',
    service_name: service,
    clinician_name: clinician,
  };
}

function parseEntry(
  value: unknown,
  typeLabel: (type: JournalEntryType) => string,
): JournalExportEntry | null {
  if (!isRecord(value)) return null;
  const clinicalDate = asString(value.entry_date);
  const createdAt = asString(value.created_at);
  if (!clinicalDate || !createdAt) return null;

  const finalizedAt = asString(value.finalized_at);
  const linked = journalDisplayAppointment(
    {
      appointment_starts_at_snapshot: asString(value.appointment_starts_at_snapshot),
      appointment_service_name_snapshot: asString(value.appointment_service_name_snapshot),
      author_name_snapshot: asString(value.author_name_snapshot),
      finalized_at: finalizedAt,
    },
    liveAppointment(value),
  );
  const serviceName = journalKindLabel({
    entryType: parseEntryType(value.entry_type),
    serviceName: linked?.service_name,
    typeLabel,
  });
  const practitionerName = linked?.clinician_name ?? asString(value.author_name_snapshot);

  return {
    clinicalDate,
    encounterAt: asString(value.encounter_at),
    appointmentAt: linked?.starts_at || asString(value.appointment_starts_at_snapshot),
    serviceName,
    practitionerName,
    status: parseStatus(value.status ?? (finalizedAt ? 'finalized' : 'draft')),
    title: asString(value.title),
    content: typeof value.content_md === 'string' ? value.content_md : '',
    createdAt,
    finalizedAt,
    attachments: asArray(value.attachments)
      .map(parseAttachment)
      .filter((row): row is JournalExportAttachment => row != null),
    amendments: asArray(value.amendments)
      .map(parseAmendment)
      .filter((row): row is JournalExportAmendment => row != null),
  };
}

function clinicalSortKey(entry: JournalExportEntry): string {
  return `${entry.encounterAt ?? entry.clinicalDate}::${entry.createdAt}`;
}

export function filterJournalExportPayloadToEntry(payload: unknown, entryId: string): unknown {
  if (!isRecord(payload)) return payload;
  return {
    ...payload,
    entries: asArray(payload.entries).filter((entry) => isRecord(entry) && entry.id === entryId),
  };
}

export function toJournalExportDocument(
  payload: unknown,
  typeLabel: (type: JournalEntryType) => string = (type) => type,
): JournalExportDocument {
  const root = isRecord(payload) ? payload : {};
  const entries = asArray(root.entries)
    .map((entry) => parseEntry(entry, typeLabel))
    .filter((entry): entry is JournalExportEntry => entry != null)
    .sort((a, b) => clinicalSortKey(a).localeCompare(clinicalSortKey(b)));

  return {
    patientName: asString(root.patient_name) ?? 'Patient',
    patientNumber: asString(root.patient_number),
    clinicName: asString(root.clinic_name) ?? asString(root.clinic_name_snapshot) ?? '',
    exportedAt: asString(root.exported_at) ?? new Date().toISOString(),
    entries,
  };
}

export function sanitizeExportName(value: string): string {
  return (
    [...value.trim()]
      .map((char) => (char.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(char) ? ' ' : char))
      .join('')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80) || 'Patient'
  );
}

function isoDateStamp(exportedAt: Date): string {
  const year = exportedAt.getFullYear();
  const month = String(exportedAt.getMonth() + 1).padStart(2, '0');
  const day = String(exportedAt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function journalExportFilename(patientName: string, exportedAt: Date): string {
  return `Patient_Journal_${sanitizeExportName(patientName)}_${isoDateStamp(exportedAt)}.pdf`;
}

export function journalExportZipFilename(patientName: string, exportedAt: Date): string {
  return `Patientjournal_${sanitizeExportName(patientName)}_${isoDateStamp(exportedAt)}.zip`;
}

export function journalExportInnerPdfFilename(patientName: string): string {
  return `Patientjournal_${sanitizeExportName(patientName)}.pdf`;
}

export function sanitizeBilagFilename(original: string): string {
  const trimmed = original.trim() || 'file';
  const lastDot = trimmed.lastIndexOf('.');
  const base = lastDot > 0 ? trimmed.slice(0, lastDot) : trimmed;
  const ext = lastDot > 0 ? trimmed.slice(lastDot) : '';
  const safeBase = sanitizeExportName(base) || 'file';
  const safeExt = [...ext]
    .filter((char) => /[A-Za-z0-9.]/.test(char))
    .join('')
    .slice(0, 16);
  return `${safeBase}${safeExt}`;
}

export function formatBilagZipFilename(number: number, originalFilename: string): string {
  const padded = String(number).padStart(2, '0');
  return `Bilag_${padded}_${sanitizeBilagFilename(originalFilename)}`;
}

function attachmentKey(attachment: JournalExportAttachment, fallback: string): string {
  return attachment.documentId ?? fallback;
}

function toBilagAttachment(
  attachment: JournalExportAttachment,
  item: JournalBilagItem,
): JournalExportAttachment {
  return {
    ...attachment,
    bilagNumber: item.number,
    zipFilename: item.zipFilename,
    zipPath: item.zipPath,
  };
}

export function assignJournalBilag(document: JournalExportDocument): {
  document: JournalExportDocument;
  bilag: JournalBilagItem[];
} {
  const bilag: JournalBilagItem[] = [];
  const byKey = new Map<string, JournalBilagItem>();

  const take = (
    attachment: JournalExportAttachment,
    fallbackKey: string,
    related: { clinicalDate: string | null; serviceName: string | null },
  ): JournalExportAttachment => {
    const key = attachmentKey(attachment, fallbackKey);
    const existing = byKey.get(key);
    if (existing) return toBilagAttachment(attachment, existing);

    const number = bilag.length + 1;
    const zipFilename = formatBilagZipFilename(number, attachment.filename);
    const item: JournalBilagItem = {
      number,
      documentId: attachment.documentId,
      filename: attachment.filename,
      mimeType: attachment.mimeType,
      createdAt: attachment.createdAt,
      uploadedAt: attachment.uploadedAt,
      uploadedBy: attachment.uploadedBy,
      notes: attachment.notes,
      zipFilename,
      zipPath: `Bilag/${zipFilename}`,
      relatedClinicalDate: related.clinicalDate,
      relatedServiceName: related.serviceName,
    };
    byKey.set(key, item);
    bilag.push(item);
    return toBilagAttachment(attachment, item);
  };

  const entries = document.entries.map((entry, entryIndex) => {
    const related = {
      clinicalDate: entry.appointmentAt || entry.encounterAt || entry.clinicalDate,
      serviceName: entry.serviceName,
    };
    return {
      ...entry,
      attachments: entry.attachments.map((attachment, attachmentIndex) =>
        take(attachment, `entry:${entryIndex}:${attachmentIndex}`, related),
      ),
      amendments: entry.amendments.map((amendment, amendmentIndex) => ({
        ...amendment,
        attachments: amendment.attachments.map((attachment, attachmentIndex) =>
          take(attachment, `amendment:${entryIndex}:${amendmentIndex}:${attachmentIndex}`, related),
        ),
      })),
    };
  });

  return {
    document: { ...document, entries },
    bilag,
  };
}

export function journalMarkdownToPlainText(markdown: string): string {
  const normalized = markdown.replace(/\r\n/g, '\n');
  const withoutFences = normalized.replace(/```([\s\S]*?)```/g, (_match, body: string) =>
    String(body).replace(/^[^\n]*\n/, ''),
  );
  return withoutFences
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
